import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { isAxiosError } from 'axios';
import { changePassword, resetPassword } from '@/api/services/user';
import { refreshUserLoggedInState } from '@/stores/auth/services';

type FormValues = {
  currentPassword?: string;
  newPassword: string;
  verifyPassword: string;
};

type UsePasswordFormControllerProps = {
  passwordResetToken?: string;
  onSuccess?: () => void;
  onError?: () => void;
  onNetworkError?: () => void;
  onResetSuccess?: () => void;
  onResetError?: () => void;
};

export const usePasswordFormController = ({
  passwordResetToken,
  onSuccess,
  onError,
  onNetworkError,
  onResetSuccess,
  onResetError,
}: UsePasswordFormControllerProps) => {
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const newPasswordFilled = watch('newPassword', '');

  useEffect(() => {
    const timer = setTimeout(async () => {
      await refreshUserLoggedInState();
    }, 0);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const submit = async (data: FormValues) => {
    if (passwordResetToken) {
      try {
        await resetPassword(data, passwordResetToken);
        onResetSuccess?.();
      } catch {
        onResetError?.();
      }
      return;
    }

    try {
      await changePassword(data);
      reset();
      onSuccess?.();
    } catch (error) {
      if (!isAxiosError(error)) {
        onNetworkError?.();
      } else {
        onError?.();
      }
    }
  };

  return {
    control,
    newPasswordFilled,
    handlePasswordSubmit: handleSubmit(submit),
  };
};
